import { useState } from 'react';
import { IDEA_LIMITS } from '@shared/validate';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { TextArea, TextInput } from '../../../components/Field';
import { tagTone } from '@shared/format';

/**
 * 创意表单，新建与编辑共用。
 *
 * 字段是照着「AI 能不能据此给出靠谱结论」设计的：
 * 只说「我想做个 XX」不叫创意，核心假设、面向谁、用什么衡量成功缺一个，
 * 研判就只能回一句「信息不足」。
 */

export interface IdeaFormValue {
  title: string;
  summary: string;
  body: string;
  hypothesis: string;
  audience: string;
  successMetric: string;
  tags: string[];
}

export const EMPTY_IDEA_FORM: IdeaFormValue = {
  title: '',
  summary: '',
  body: '',
  hypothesis: '',
  audience: '',
  successMetric: '',
  tags: [],
};

export function IdeaForm({
  initial,
  tagOptions,
  submitting,
  submitLabel = '提交并进入看板',
  onSubmit,
  onCancel,
}: {
  initial?: Partial<IdeaFormValue>;
  tagOptions: readonly string[];
  submitting: boolean;
  submitLabel?: string;
  onSubmit: (value: IdeaFormValue, status: 'draft' | 'submitted') => void;
  onCancel?: () => void;
}) {
  const [value, setValue] = useState<IdeaFormValue>({ ...EMPTY_IDEA_FORM, ...initial });
  const [customTag, setCustomTag] = useState('');

  const patch = (part: Partial<IdeaFormValue>) => setValue((current) => ({ ...current, ...part }));

  const toggleTag = (tag: string) => {
    const active = value.tags.includes(tag);
    if (!active && value.tags.length >= IDEA_LIMITS.tagsMax) return;
    patch({ tags: active ? value.tags.filter((item) => item !== tag) : [...value.tags, tag] });
  };

  const addCustomTag = () => {
    const tag = customTag.trim().replace(/^#/, '');
    if (tag === '' || tag.length > IDEA_LIMITS.tagMax) return;
    if (value.tags.includes(tag) || value.tags.length >= IDEA_LIMITS.tagsMax) return;
    patch({ tags: [...value.tags, tag] });
    setCustomTag('');
  };

  const suggestionTags = tagOptions.filter((tag) => !value.tags.includes(tag));

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(value, 'submitted');
      }}
    >
      <TextInput
        label="标题"
        required
        maxLength={IDEA_LIMITS.titleMax}
        placeholder="一句话说清你要做什么"
        value={value.title}
        onChange={(event) => patch({ title: event.target.value })}
      />
      <TextInput
        label="一句话说明"
        required
        maxLength={IDEA_LIMITS.summaryMax}
        placeholder="给同事看的电梯陈述：做什么、给谁、为什么现在做"
        value={value.summary}
        onChange={(event) => patch({ summary: event.target.value })}
        hint={`${value.summary.length}/${IDEA_LIMITS.summaryMax} · 提交时至少 ${IDEA_LIMITS.summaryMin} 个字`}
      />
      <TextArea
        label="详细说明"
        required
        rows={8}
        maxLength={IDEA_LIMITS.bodyMax}
        placeholder="背景、现状的痛点、你打算怎么做、已经有哪些线索。写得越具体，研判越有依据。"
        value={value.body}
        onChange={(event) => patch({ body: event.target.value })}
        hint={`${value.body.length}/${IDEA_LIMITS.bodyMax} · 提交时至少 ${IDEA_LIMITS.bodyMin} 个字`}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <TextArea
          label="核心假设"
          required
          rows={3}
          maxLength={IDEA_LIMITS.hypothesisMax}
          placeholder="如果……那么……。把可被证伪的那句话写出来。"
          value={value.hypothesis}
          onChange={(event) => patch({ hypothesis: event.target.value })}
        />
        <TextArea
          label="面向谁"
          required
          rows={3}
          maxLength={IDEA_LIMITS.audienceMax}
          placeholder="具体到角色或客户类型，不要写「所有用户」。"
          value={value.audience}
          onChange={(event) => patch({ audience: event.target.value })}
        />
      </div>

      <TextArea
        label="用什么指标衡量成功"
        required
        rows={2}
        maxLength={IDEA_LIMITS.successMetricMax}
        placeholder="例如：把巡检报告的人工整理时间从 2 小时降到 20 分钟。"
        value={value.successMetric}
        onChange={(event) => patch({ successMetric: event.target.value })}
      />

      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-fg-muted">
          标签
          <span className="ml-1 font-normal text-fg-subtle">
            （最多 {IDEA_LIMITS.tagsMax} 个，用于看板筛选）
          </span>
        </span>

        {value.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {value.tags.map((tag) => (
              <button key={tag} type="button" onClick={() => toggleTag(tag)} title="点击移除">
                <Badge tone={tagTone(tag)}>{tag} ✕</Badge>
              </button>
            ))}
          </div>
        ) : null}

        {suggestionTags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {suggestionTags.map((tag) => (
              <button key={tag} type="button" onClick={() => toggleTag(tag)} title="点击添加">
                <Badge tone="neutral">+ {tag}</Badge>
              </button>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <TextInput
            placeholder="也可以自己写一个标签"
            value={customTag}
            maxLength={IDEA_LIMITS.tagMax}
            onChange={(event) => setCustomTag(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              addCustomTag();
            }}
          />
          <Button variant="ghost" size="sm" onClick={addCustomTag} disabled={customTag.trim() === ''}>
            添加
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <Button type="submit" variant="primary" loading={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="default" loading={submitting} onClick={() => onSubmit(value, 'draft')}>
          存为草稿
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            取消
          </Button>
        ) : null}
        <span className="text-[11px] text-fg-subtle">
          草稿只有你自己可见；提交后全员可见，并可以发起 AI 研判。
        </span>
      </div>
    </form>
  );
}
